import { WidgetState } from '@common/AppBuilder/types';
import { EntityIconType } from '@common/Entity/constants';
import { ConnectionSpec } from '@common/Fabric/Connections/types';
import { SqlProcessKind } from '@common/graph/types';
import { FilterTypeV2 } from '@common/interims/conditionTypes';
import {
  BaseMetaInfo,
  BaseProcess,
  BaseProcessMetadata,
  BaseSourceSuggestion,
  BaseState,
  COMMON_ACTION_TYPES,
  Connection,
  GenericGraph,
  Metadata,
  GemSpec,
  GemType
} from '@common/types';
import { COLUMNS } from '@common/types/columnsEnum';
import { ComponentProgress } from '@common/types/ComponentStatus';
import { Entity } from '@common/types/Entity';
import { UISpecElement } from '@common/types/specTypes';
import { ProcessUnitTest } from '@pages/Sql/UnitTest/types';
import { ObjectLiteral, OneOf } from '@prophecy/interfaces/generic';
import { BackEndCodeLanguage } from 'frontend/core/src/graphqlTypes/enums';
import {
  DataType,
  SQLType,
  ProviderColumnTypes,
  SQLStructField,
  FixedWidthOptions,
  AdditionalPropertiesType
} from 'frontend/core/src/schema/sqlTypes';
import { ColumnGeneration, FixedWidthMetadata, MappingType, ProviderType } from 'frontend/core/src/schema/types';
import { JSONSchema4 } from 'json-schema';

import { actionTypes } from '../../../redux/action-types';
import { Port } from '../../../redux/portTypes';
import { CompilationStatus } from '../../../redux/types';
import { ConfigurationSchema, Field, RecordType } from '../../../schema/configTypes';
import {
  SQL_CURRENT_MODAL_GRAPH_PATH,
  SQL_CURRENT_PIPELINE_GRAPH_PATH,
  SQL_CURRENT_SINGULAR_DATA_TEST_GRAPH_PATH
} from '../bindings';
import { MultipleSensorScheduleInfo } from '../Graph/SchedulePipeline/types';
import { EnvironmentTreeNode } from '../ProjectBrowser/types';
import { ExternalSourceType } from './ExternalSourceType';

export const DBT_VERSION = 2 as const;

export const ArrowProviderType = 'arrow';

export enum sqlActionTypes {
  lineageNodeCounts = 'lineage/nodeCount',
  typesDidOpen = 'types/didOpen',
  toggleDependenciesModal = 'toggleDependenciesModal',
  orchestrationComponentDidOpen = 'orchestrationComponentDidOpen',
  updateComponentProgress = 'updateComponentProgress',
  markRunningComponentsSuccess = 'markRunningComponentsSuccess',
  markRunningComponentsFailed = 'markRunningComponentsFailed',
  updateOrchestrationConnectionsTreeState = 'updateOrchestrationConnections/TreeState',
  updateOrchestrationConnectionsSource = 'updateOrchestrationConnections/Source',
  setFabricId = 'setFabricId',
  setFabricSessionId = 'setFabricSessionId',
  setIsLanguageChangeInProgress = 'setIsLanguageChangeInProgress'
}

export enum SourceType {
  Source = 'Source',
  UnreferencedSource = 'UnreferencedSource',
  ExternalSource = 'ExternalSource',
  Seed = 'Seed',
  Model = 'Model',
  Snapshot = 'Snapshot',
  Dataset = 'Dataset',
  Table = 'Table'
}

export type SourceTypeType = `${SourceType}`;

export enum EntityAction {
  create = 'create',
  edit = 'edit',
  view = 'view',
  preview = 'preview'
}

export const ProcessKindsWithHiddenOutput = [
  SqlProcessKind.TargetModel,
  SqlProcessKind.TargetSnapshot,
  SqlProcessKind.SingularDataTest
];

export const EntityToGenericEntityMap = {
  [Entity.Model]: Entity.Model,
  [Entity.StoredProcedure]: Entity.StoredProcedure,
  [Entity.Seed]: Entity.Dataset,
  [Entity.Macro]: Entity.Function,
  [Entity.Source]: Entity.Dataset,
  [Entity.Dataset]: Entity.Dataset,
  [Entity.UnreferencedSource]: Entity.Dataset,
  [Entity.UnreferencedTable]: Entity.Dataset,
  [Entity.View]: Entity.Dataset,
  [Entity.Table]: Entity.Dataset,
  [Entity.Snapshot]: Entity.Snapshot,
  [Entity.SingularDataTest]: Entity.Test,
  [Entity.GenericDataTest]: Entity.GenericDataTest,
  [Entity.ERD]: Entity.ERD
};

export const EntityToGraphPath = {
  [Entity.Pipeline]: SQL_CURRENT_PIPELINE_GRAPH_PATH,
  [Entity.Model]: SQL_CURRENT_MODAL_GRAPH_PATH,
  [Entity.Snapshot]: SQL_CURRENT_MODAL_GRAPH_PATH,
  [Entity.SingularDataTest]: SQL_CURRENT_SINGULAR_DATA_TEST_GRAPH_PATH
};

export const GraphEntity = [Entity.Model, Entity.Snapshot, Entity.SingularDataTest, Entity.Pipeline] as const;

export type GraphEntityType = OneOf<typeof GraphEntity>;

export enum StoreProcedureMode {
  'IN' = 'IN',
  'OUT' = 'OUT',
  'INOUT' = 'INOUT'
}

export type StoredProcedureParameter = {
  name: string;
  kind: string; // this will same as field.type in schema
  mode?: `${StoreProcedureMode}`;
};
export enum StaleState {
  none = 'none',
  stale = 'file',
  graph = 'graph'
}

export type ExternalSources = {
  type: ExternalSourceType;
  name: string;
  children?: ExternalSources[];
  tables?: Record<string, SQLType>;
};
// for graph other than pipeline graph, component type is seed for seed type source
export type DatasetProcessType =
  | SqlProcessKind.Source
  | SqlProcessKind.Dataset
  | SqlProcessKind.Seed
  | SqlProcessKind.ExternalSource;
export type SourceInfo = {
  source: string;
  tableName: string;
  sourceName?: string;
  sourceType: SourceTypeType;
  datasetProcessType?: `${DatasetProcessType}`;
};
type SqlSuggestionExtraProperties = {
  source: {
    table: {
      name: string;
      sourceType: SourceTypeType;
      sourceName: string;
    };
  };
  sourceComponentKind: SourceTypeType;
};
type SqlSuggestionProperties = BaseSourceSuggestion & SqlSuggestionExtraProperties;
export type SourceSuggestions = {
  sources: SqlSuggestionProperties[];
};

export interface Mapping {
  type: string;
  target: {
    database: string;
    schema: string;
    name: string;
    column: string;
  };
}
export type ColumnTest = ModelDataTestType;
export type Column = {
  name: string;
  dataType: DataType;
  description?: string;
  fixedWidthOptions?: FixedWidthOptions;
  fixedWidthMetadata?: FixedWidthMetadata;
  metadata?: {
    columnGeneration?: ColumnGeneration;
  };
  tags?: string[];
  quote?: boolean;
  tests?: ColumnTest[];
  mappings?: MappingType[];
};

export type DefaultDataTestType = { column_name?: string; values?: string[]; to?: string; field?: string };

export type ModelDataTestType = Record<string, DefaultDataTestType> | string;

export type UnreferencedSource = {
  name: string;
  columns: Column[];
  providerType: ProviderType;
};

export type EntityToSync = {
  name: string;
  type: SourceType;
  sourceName?: string;
  syncedOnce?: boolean;
};

export type SqlPipelineScheduleInfo = {
  pipelineParameterSet: string;
  cron: string;
  timeZone: string;
  emailOnSuccess: boolean;
  emailOnFailure: boolean;
  emailOnStart: boolean;
  emails: string[];
  enabled: boolean;
  runGenericDataTests?: boolean;
  runSingularDataTests?: string[];
} | null;

export type SqlSensorScheduleInfo = {
  enabled: boolean;
  triggers: MultipleSensorScheduleInfo;
  isDefaultView: boolean;
} | null;

export type ParameterSet = {
  name: string;
  values: Record<string, string>;
};

export type SQLConfiguration = {
  schema: {
    type: 'record';
    fields: Field[];
  };
  parameterSets?: ParameterSet[];
  currentParameterSet: string;
};

export type PreviewSchema = {
  fields: SQLStructField[];
  providerType: string;
  entityType: string;
};

export type PreviewSchemaType = {
  id: string;
  label: string;
  tableName: string;
  tableSourceName: string;
  schema: PreviewSchema;
};
export type PipelineMetainfo = {
  config?: SQLConfiguration;
  id: string;
  name: string;
  path: string;
  propertyPath: string;
  preview: Array<PreviewSchemaType>;
  pushToAnalysisGems: PreviewSchemaType[];
  sources: Array<{ id: string; label: string }>;
  hasNonSQLGems?: boolean;
};

export type StoredProcedureOptions = {
  strictMode: boolean;
  description: string;
};

type StoredProcedureLocation = {
  database?: string;
  schema?: string;
};

export enum StoredProcedureLanguage {
  SQL = 'SQL',
  JAVASCRIPT = 'JAVASCRIPT',
  PYTHON = 'PYTHON'
}

export type StoredProcedure = {
  name: string;
  location?: StoredProcedureLocation;
  definition: string;
  body: string;
  filePath: string;
  parameters: StoredProcedureParameter[];
  options?: StoredProcedureOptions;
  returnType?: string;
  language?: `${StoredProcedureLanguage}`;
  staleState: StaleState;
};

export type SQLMetaInfo = BaseMetaInfo & {
  fabricType?: 'Prophecy' | 'Sql';
  fileSystem: Folder;
  autoLayout?: boolean;
  providerType: string;
  label: string;
  name: string;
  backEndLanguage?: BackEndCodeLanguage;
  externalSources: ExternalSources;
  unreferencedSources?: {
    sources: UnreferencedSource[];
  };
  fabricDetails: {
    schema: string;
    wareHouse: string;
    db: string;
  };
  datasets: Record<string, Dataset>;
  storedProcedures?: Record<string, StoredProcedure>;
  sourceSuggestions?: SourceSuggestions;
  staleState?: StaleState;
  disableVisualView?: boolean;
  dependenciesContainer?: {
    dependencies?: Record<string, Dependency>;
  };
  entitiesToSync?: EntityToSync[];
  schedules?: SqlPipelineSchedule[];
  schedule?: SqlPipelineScheduleInfo;
  sensorSchedule?: SqlSensorScheduleInfo;

  configuration?: SQLConfiguration;
  pipelines?: Record<string, PipelineMetainfo>;
  manualCompilation?: boolean;
  processesVsModelFiles?: Record<string, string>;
};

export type SqlPipelineSchedule = {
  name: string;
  pipelineParameterSet: string;
  scheduleType: 'cron' | 'sensor';
  enabled: boolean;
  cron: string;
  timeZone: string;
  emailOnSuccess: boolean;
  emailOnFailure: boolean;
  emailOnStart: boolean;
  emails: string[];
  runGenericDataTests?: boolean;
  runSingularDataTests?: string[];
  triggers: MultipleSensorScheduleInfo;
  isDefaultView: boolean;
};
export type SqlEntityGraphMetaInfoProperties = {
  columns?: Column[];
  tests?: ModelDataTestType[];
  description?: string;
  name: string;
};

export type SQLEntityGraphMetaInfo = SQLMetaInfo & {
  graphConfig?: EntityGraphConfig;
  properties?: SqlEntityGraphMetaInfoProperties;
};

export enum FileSystemNodeType {
  Folder = 'Folder',
  File = 'File',
  Job = 'Job'
}

export enum FileTypeEnum {
  Model = 'Model',
  Snapshot = 'Snapshot',
  Seed = 'Seed',
  YAML = 'YAML',
  Macro = 'Macro',
  ProphecyJson = 'ProphecyJson',
  RawFile = 'RawFile',
  MacroSpecFile = 'MacroSpecFile',
  SingularDataTest = 'SingularDataTest',
  RawPythonFile = 'RawPythonFile',
  GenericDataTest = 'GenericDataTest',
  StoredProcedure = 'StoredProcedure',
  Pipeline = 'Pipeline',
  App = 'App',
  AppPython = 'AppPython'
}

export type FileType = `${FileTypeEnum}`;

export type CurrentFile = { propertyPath: string; type: FileType | FileSystemNodeType.Job; isTempModel?: boolean };

export const TypeExtensionMap = {
  [FileTypeEnum.Pipeline]: 'py',
  [FileTypeEnum.Model]: 'sql',
  [FileTypeEnum.Snapshot]: 'sql',
  [FileTypeEnum.Seed]: 'csv',
  [FileTypeEnum.YAML]: 'yml',
  [FileTypeEnum.Macro]: 'sql',
  [FileTypeEnum.MacroSpecFile]: 'py',
  [FileTypeEnum.RawPythonFile]: 'py',
  [FileTypeEnum.GenericDataTest]: 'sql',
  [FileTypeEnum.SingularDataTest]: 'sql',
  [FileTypeEnum.StoredProcedure]: 'sql',
  [FileTypeEnum.ProphecyJson]: 'json',
  [FileTypeEnum.RawFile]: '',
  [FileTypeEnum.App]: 'json',
  [FileTypeEnum.AppPython]: 'py'
} as const;

export type File = {
  content: string;
  type: FileType;
  name: string;
  extension?: string;
  folderConfiguration?: FolderConfig;
  projectConfiguration?: ProjectConfig;
  path: string;
  macros?: FileMacro[];
  tests?: FileTest[];
  editable?: boolean;
};
export type MacroSpecFile = {
  content: string;
  category: string;
  editable?: boolean;
  macroName: string;
  name: string;
  path: string;
  projectName: string;
  type: FileTypeEnum.MacroSpecFile;
};

export enum FolderConfigType {
  namespacedConfig = 'namespacedConfig',
  resourceConfig = 'resourceConfig'
}

export type NamespacedConfig = {
  [FolderConfigType.namespacedConfig]: Record<string, NamespacedConfig>;
  [FolderConfigType.resourceConfig]: ObjectLiteral;
};

export type ProjectConfig = {
  dbtDefined: {
    dbtProjConf: ObjectLiteral;
    models: NamespacedConfig;
    seeds: NamespacedConfig;
    sources: NamespacedConfig;
    tests: NamespacedConfig;
  };
  userDefined?: {
    vars: ObjectLiteral;
    type: 'ProjectLevelUserDefinedConfig';
  };
};

export type FolderEntityConfig = {
  name: string;
  description: string;
  columns: [];
  tests?: ModelDataTestType[];
  tags?: string[];
  config?: {};
};

export type TableConfig = {
  name: string;
  description?: string;
  columns?: Column[];
  tests?: ModelDataTestType[];
  additionalProperties?: AdditionalPropertiesType;
};

type SourceConfig = FolderEntityConfig & {
  database?: string;
  schema?: string;
  tables: TableConfig[];
  providerType: ProviderType;
};

export type FolderConfig = {
  models?: FolderEntityConfig[];
  snapshots?: FolderEntityConfig[];
  sources: SourceConfig[];
  singularDataTests?: FolderEntityConfig[];
  version?: number;
};

export type EntityGraphConfig = {
  entityConfig?: ObjectLiteral;
  pysparkConfig?: ObjectLiteral;
  mergeApproach?: string;
  parallelism?: number;
  batchSize?: number;
  userDefinedConfig?: {
    vars: Record<string, string>;
    //TODO: Change the type to `EntityLevelUserDefinedConfig` from backend.
    type: 'ModelLevelUserDefinedConfig';
  };
};

export type Folder = {
  name: string;
  type: FileSystemNodeType.Folder;
  config?: ProjectConfig | FolderConfig;
  children: {
    [key: string]: FileSystemNode;
  };
  editable?: boolean;
  path: string;
};

export type JobFolder = {
  name: string;
  type: FileSystemNodeType.Job;
  jobId: string;
  path: string;
  editable?: boolean;
  children: {
    [key: string]: Folder | File;
  };
};

export type FileSystemNode = Folder | File | JobFolder | MacroSpecFile;

export type ProcessMetadata = BaseProcessMetadata & {
  autoLayout?: boolean;
  macroDependencies?: string[];
};

export enum MacroToVisualType {
  expression = 'Function',
  query = 'Gem',
  test = 'Test'
}

export type Macro = {
  name: string;
  file: string;
  description: string;
  macroType: MacroType;
  parameters: ConfigurationSchema;
  definition: string;
  staleState: StaleState;
  macroDependencies: string[];
};

export type GenericTestType = {
  name: string;
  file: string;
  id: number;
  description: string; // as comment above test definition
  parameters: ConfigurationSchema; // without types
  definition: string;
  schema?: { parameters: ConfigurationSchema };
};
export type GemPreview = {
  name: string;
  category: string;
  preview: SqlBaseGraph;
  generatedCode: string;
};
export type FileMacro = Omit<Macro, 'description' | 'file'>;
type FileTest = Omit<GenericTestType, 'file'>;

export enum UploadType {
  table = 'table',
  source = 'source'
}

export type SQLAppInstance = {
  config: Record<string, unknown>;
  sources: Record<
    string,
    {
      type: UploadType;
      fileName: string;
      process: unknown;
    }
  >;
  filters?: Record<string, FilterTypeV2>;
  schedule?: SqlPipelineScheduleInfo;
};

export type SQLApp = {
  id: string;
  metainfo: {
    pipelineId: string;
    description?: string;
    settings?: {
      autoRunEnabled?: boolean;
    };
    parameterSet?: string;
  };
  definition?: {
    template: WidgetState[];
  };
  instances?: Record<string, SQLAppInstance>;
};

export type SqlBaseGraph = GenericGraph<SQLMetaInfo, ProcessMetadata, SQLProcess, Connection>;
export type SqlEntityGraph = GenericGraph<SQLEntityGraphMetaInfo, ProcessMetadata, SQLProcess, Connection>;
export type SqlGraph = SqlBaseGraph & {
  currentPipelineGraph: SqlEntityGraph;
  currentModelGraph: SqlEntityGraph;
  currentSnapshotGraph?: SqlEntityGraph;
  currentSingularDataTestGraph?: SqlEntityGraph;
  currentMacro?: Macro;
  currentGem?: GemPreview;
  currentGenericDataTest?: Macro;
  currentTest?: Macro;
  currentApp?: SQLApp;
};

export type EffectRootGraph =
  | SqlGraph['currentModelGraph']
  | SqlGraph['currentPipelineGraph']
  | SqlGraph['currentSnapshotGraph']
  | SqlGraph['currentSingularDataTestGraph'];
export type ConfigSchemas = {
  folder: JSONSchema;
  project: JSONSchema;
};

export type CompressionType = 'uncompressed' | 'gzip' | 'zstd' | 'lz4' | 'zlib' | 'snappy' | 'lzop';
export type CompressionValue = CompressionType | undefined | '';

export type OrchestrationSourceProperties = {
  isNew?: boolean;
  connector: {
    // properties related to selected connection
    kind: string;
    properties: {
      id?: string;
    };
  };
  compression: {
    kind?: CompressionValue;
  };
  format: {
    kind: string;
    category: string;
    properties: object; // properties related to format
  };
  properties: object; //  properties related to location
};
export type SourceSpec = {
  kind: string;
  connectorLabel: string;
  connectorKind: string; // kind of connection (location type in dialog)
  formatKind: string; // kind of format (format in dialog)
  formatCategory: string; // category  (type in dialog)
  dialogJson: {
    tabs: { title: string; spec: UISpecElement }[];
  };
  defaultState: {
    properties: OrchestrationSourceProperties;
  };
  stateSchema: GemSpec['stateSchema'];
  helpDocument: string;
  description: string;
  disabled: boolean;
  tooltip: string;
  isExaminable?: boolean;
};

export type OrchestrationConnectionsData = {
  data: EnvironmentTreeNode[];
  state: {
    expandedKeys: React.Key[];
    loadingKeys: React.Key[];
    loadedKeys: React.Key[];
  };
};
export interface SqlState extends BaseState<SqlGraph, Metadata> {
  sessionId: string;
  selectedFabricId?: string;
  providerColumnTypes: ProviderColumnTypes;
  compilationStatus: {
    [processId: string]: CompilationStatus;
  };
  lineageNodeCounts?: number;
  isDependenciesModalOpen?: boolean;
  orchestration: {
    // spec for connections, used to create connection
    connections: ConnectionSpec[];
    gems: GemSpec[];

    // spec for datasets, used in source/target (properties tab)
    sources: SourceSpec[];
    targets: SourceSpec[];
  };
  componentProgress: {
    [componentId: string]: ComponentProgress;
  };
  orchestrationConnectionsData: {
    [fabricId: string]: OrchestrationConnectionsData;
  };
  fabricSessionId?: string;
  isSQLSetupInProgress: boolean;
  isPythonSetupInProgress: boolean;
}

type Sql_Action =
  | {
      type: sqlActionTypes.orchestrationComponentDidOpen;
      payload: { gems: GemSpec[]; connections: ConnectionSpec[]; sources: SourceSpec[]; targets: SourceSpec[] };
    }
  | {
      type: sqlActionTypes.lineageNodeCounts;
      payload: number;
    }
  | {
      type: sqlActionTypes.toggleDependenciesModal;
      payload: boolean;
    }
  | {
      type: actionTypes.compileProgress;
      payload: { path: string; status: CompilationStatus; updateChildProcess?: boolean }[];
    }
  | {
      type: sqlActionTypes.typesDidOpen;
      payload: {
        types: ProviderColumnTypes;
      };
    }
  | {
      type: sqlActionTypes.updateComponentProgress;
      payload: Array<{ componentId: string; progress: Partial<ComponentProgress> }>;
    }
  | {
      type: sqlActionTypes.markRunningComponentsSuccess;
    }
  | {
      type: sqlActionTypes.markRunningComponentsFailed;
    }
  | {
      type: sqlActionTypes.updateOrchestrationConnectionsSource;
      payload: { fabricId: string; treeData: EnvironmentTreeNode[] };
    }
  | {
      type: sqlActionTypes.updateOrchestrationConnectionsTreeState;
      payload: { fabricId: string; state: Partial<OrchestrationConnectionsData['state']> };
    }
  | {
      type: sqlActionTypes.setFabricSessionId;
      payload: { fabricSessionId: string };
    }
  | {
      type: sqlActionTypes.setIsLanguageChangeInProgress;
      payload: { isSQLSetupInProgress: boolean; isPythonSetupInProgress: boolean };
    };

export type SQL_ACTION_TYPES = Sql_Action | COMMON_ACTION_TYPES<SqlGraph, Metadata, SqlState>;
export type SQlInitializeParams = {
  projectId: string;
  branch: string;
  selectedFabricId?: string;
  currentModel?: string;
  currentPipeline?: string;
  currentSnapshot?: string;
  currentSingularDataTest?: string;
  versionedRdcMergeMode?: string;
  nonVersionedRdcMergeMode?: string;
  commitId?: string | null;
  forceInitFromMetadata?: boolean;
  mode?: string;
};
interface UnitTests {
  unitTests: ProcessUnitTest[];
}
export type SQLProcess = BaseProcess<ProcessMetadata> & {
  test?: UnitTests;
};

export type SQLProcessProperties = {
  format?: {
    properties?: {
      schema?: {
        fields?: Column[];
      };
      description?: string;
      additionalProperties?: AdditionalPropertiesType;
    };
  };
};

export type JSONSchema = JSONSchema4;
export type ConfigOptions = {
  name: string;
  schema: JSONSchema4;
}[];

export type FileMap = {
  [key: string]: { file: Folder | JobFolder | File | MacroSpecFile; propertyPath: string; isTempModel?: boolean };
};

export enum PeriodData {
  day = 'day',
  minute = 'minute',
  hour = 'hour'
}

export type EntityType = {
  name: string;
  filePath: string;
  entity?: FileTypeEnum;
  path: string;
  content?: string;
  key?: string;
  isTempModel?: boolean;
};

type TableEntity = EntityType & {
  tableIdx: number;
  srcIdx: number;
  columns?: Column[];
};

export type SourceEntityType = EntityType & {
  tables: TableEntity[];
  srcIdx: number;
};

export type MacroEntityType = EntityType & {
  macroType: MacroType;
  macroIndex: number;
};

export type AppEntityType = EntityType & {};

export type GenericTestEntityType = EntityType &
  FileTest & {
    index: number;
    projectName?: string;
    testType?: Entity;
  };

export type JobEntityType = EntityType & {
  jobId: string;
  enabled: boolean;
  scheduleCron?: string;
  projects?: string[];
  models?: string[];
  scheduleHelp: string;
};
export type GemEntityType = {
  category: EntityIconType;
  macroName: string;
  project: string;
  type: FileTypeEnum.MacroSpecFile;
} & EntityType;

export type PipelineEntityType = EntityType & {};

export type SqlEntity =
  | Entity.StoredProcedure
  | Entity.Dataset
  | Entity.Pipeline
  | Entity.Macro
  | Entity.Model
  | Entity.Seed
  | Entity.Snapshot
  | Entity.Job
  | Entity.Source
  | Entity.UnreferencedSource
  | Entity.UnreferencedTable
  | Entity.Table
  | Entity.View
  | Entity.SingularDataTest
  | Entity.GenericDataTest
  | Entity.MarkdownDocument
  | Entity.App
  | Entity.ERD
  | Entity.File;

export type CurrentEntity = {
  entity?: SqlEntity;
  id?: string;
  isSnapshotMerge?: boolean;
  name?: string;
  isSpecFile?: boolean;
};

export const FileTypeNotMergeable = [Entity.Model, Entity.Snapshot, Entity.Seed, Entity.SingularDataTest];

export type OrchestrationSourceType = {
  id: string;
  kind: string; // kind of connection (location type in dialog)
  metadata: BaseProcessMetadata;
  properties: OrchestrationSourceProperties;
  ports?: {
    inputs?: Port[];
    outputs?: Port[];
    isCustomOutputSchema?: boolean;
  };
} & Omit<GemType, 'componentInfo'>;

export type OrchestrationTargetType = OrchestrationSourceType;

export type DatasetProcess = {
  id: string;
  kind: string;
  metadata: BaseProcessMetadata;
  properties: {
    table: {
      name: string;
      sourceType: 'Source' | 'View' | 'Seed';
      sourceName: string;
      alias: string;
    };
  };
};

export type PipelineModelNodeProperties = {
  modelName: string;
};
export type PipelineSnapshotNodeProperties = {
  snapshotName: string;
};
export type CommentNodeProperties = {
  connectedNodes?: string[];
};
export type StoredProcedureIdentifier = `${string}.${string}` | `${string}.${string}.${string}` | string;
export type StoredProcedureNodeProperties = {
  storedProcedureIdentifier: StoredProcedureIdentifier;
  passThroughColumns: string[]; // TODO: to be updated with correct type
  parameters: Record<string, string>; // TODO: to be updated with correct type
};

export type DatasetSeed = {
  kind: Entity.Seed;
  name: string;
  content: string;
  seedProperties: {
    name: string;
    description: string;
    columns: Column[];
  };
};

export type DatasetTable<Kind extends Entity = Entity.Table> = {
  kind: Kind;
  name?: string;
  sourceProperties: {
    name: string;
    providerType: ProviderType;
    schema?: string;
    database?: string;
  };
  tableProperties: {
    name?: string;
    description?: string;
    type: string;
    tests?: ModelDataTestType[];
    columns?: Column[];
  };
  newDatasetProperties?: unknown;
};

export type DatasetView = DatasetTable<Entity.View>;

export type DatasetUnreferencedTable = DatasetTable<Entity.UnreferencedTable>;
export type Dataset = { id: string } & (DatasetSeed | DatasetTable | DatasetView | DatasetUnreferencedTable);
export type ProjectEntitiesType = {
  pipelines: EntityType[];
  models: EntityType[];
  seeds: EntityType[];
  genericDataTests: GenericTestEntityType[];
  gems: GemEntityType[];
  sources: SourceEntityType[];
  macros: MacroEntityType[];
  singularDataTests: EntityType[];
  jobs: JobEntityType[];
  documents: EntityType[];
  apps: EntityType[];
  storedProcedures: EntityType[];
};
export enum MacroType {
  expression = 'expression',
  query = 'query'
}
export interface Dependency {
  name: string;
  version: string;
  entities: DependencyEntities;
  dependents: string[];
  prophecyProjectId?: string;
  packageConfiguration?: {
    packages: [DependencyData];
  };

  ymlFiles?: {
    folderConfiguration?: FolderConfig;
  }[];
}

interface DependencyEntities {
  pipelines: DependencyPipeline[];
  models: DependencyModel[];
  seeds: DependencySeed[];
  snapshots: DependencyModel[];
  macros: DependencyMacro[];
  sources: DependencySource[];
  tests: GenericTestEntityType[];
  gems: {
    specs: { name: string; projectName: string; filePath: string; category: string }[];
  };
  apps: DependencyApp[];
}

interface DependencyFile {
  filePath: string;
}

interface DependencySeed {
  name: string;
  files: DependencyFile[];
  path?: string;
  filePath?: string;
  schema: { fields: COLUMNS[] };
}

interface DependencyPipeline {
  name: string;
  code: string;
  path?: string;
  filePath?: string;
  files: DependencyFile[];
  schema: { fields: COLUMNS[] };
}

interface DependencyApp {
  filePath: string;
  name: string;
  path: string;
}
interface DependencyModel {
  name: string;
  code: string;
  path?: string;
  filePath?: string;
  files: DependencyFile[];
  schema: { fields: COLUMNS[] };
}

interface DependencyMacro {
  name: string;
  code: string;
  files: DependencyFile[];
  category?: string;
  macroType: MacroType;
  parameters: RecordType;
}

interface DependencySource {
  name: string;
  database: string;
  schema: string;
  files: File[];
  tables: [
    {
      name: string;
      schema: { fields: Column[] };
      description?: string;
      tableType: 'table' | 'view';
    }
  ];
}

export type DependencyData = {
  package?: string;
  version?: string[];
  git?: string;
  revision?: string;
  subdirectory?: string;
  ['warn-unpinned']?: boolean;
  prophecyProjectId?: string;
  autoUpgrade?: boolean;
  deleteAllowed?: boolean;
  prophecyProjectName?: string;
};

export type ExamineSpecResponse = {
  type: 'Examined';
  processId: string;
  specJson: Record<string, unknown>;
  schema?: SQLType;
};

// BigQuery table layout API response type
export type TableLayoutResponse = {
  exists: boolean;
  layout?: {
    tableDescription?: string;
    partition?: {
      type?: string;
      field?: string;
      granularity?: string;
      requirePartitionFilter?: boolean;
      expirationDays?: number;
    };
    clustering?: {
      fields?: string[];
    };
  };
};
